/*
 * NoticeModal
 * @module components
 * @flow
 */

import React from 'react';
import { Modal, StyledButton } from '~/components';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components/macro';

import messages from './messages';

export type NoticeModalProps = {
  header?: React$Node | string,
  content?: React$Node | string,
  button?: React$Node,
  buttonLabel?: string,
  isVisible?: boolean,
  onClose?: () => void,
  onButtonClick?: () => void,
};

const NoticeModalContent = styled.div`
  position: relative;
  min-width: 364px;
  min-height: 153px;

  @media (max-width: 450px) {
    min-width: 300px;
  }
`;

const NoticeModalHeader = styled.h2`
  margin: 7px 0 20px;
  font-size: 20px;
  font-weight: 600;
  text-align: center;
`;

const NoticeModalButtonWrapper = styled.div`
  width: 100%;
  text-align: center;
`;

const NoticeModalButton = styled(StyledButton)`
  padding: 0 32px;
  min-width: 98px;
`;

/**
 * NoticeModal React-component
 * @memberof components
 * @param {React$Node | string} [props.header] - header
 * @param {React$Node | string} [props.content] - content
 * @param {React$Node} [props.button] - button
 * @param {string} [props.buttonLabel] - button label
 * @param {boolean} [props.isVisible] - flag for showing modal
 * @param {Function} [props.onClose] - handler for modal closing
 * @param {Function} [props.onButtonClick] - handler for button click
 * @example <caption>Simple usage</caption>
 * <NoticeModal>{content}<NoticeModal/>
 * @returns {React$Node} React-component
 */
export const NoticeModal = ({
  header,
  content,
  button,
  buttonLabel,
  isVisible,
  onClose,
  onButtonClick,
}: NoticeModalProps) => (
  <Modal.Portal onClose={onClose} isVisible={isVisible}>
    <NoticeModalContent>
      <NoticeModalHeader>
        {header || <FormattedMessage {...messages.defaultTitle} />}
      </NoticeModalHeader>
      {content}
      {button ? (
        button
      ) : (
        <NoticeModalButtonWrapper>
          <NoticeModalButton palette="primary" onClick={onButtonClick} small>
            {buttonLabel || (
              <FormattedMessage {...messages.defaultButtonLabel} />
            )}
          </NoticeModalButton>
        </NoticeModalButtonWrapper>
      )}
    </NoticeModalContent>
  </Modal.Portal>
);

NoticeModal.defaultProps = {
  onClose: () => {},
  onButtonClick: () => {},
};
